import { getGameRuntimeConfig } from "./config";
import { CLIENT_BUSINESS_CODE, RUNTIME_TRANSPORT_CODE } from "./business-codes";
import { ensureRuntimeEndpointsReady, recoverRuntimeEndpoint } from "./runtime-endpoints";

export interface HttpOptions {
  token?: string;
  body?: unknown;
  signal?: AbortSignal;
  // 只有服务端具备幂等保护的 POST 才能显式允许跨域重试，避免重复下单或重复扣减月亮。
  retryAcrossOrigins?: boolean;
}

const FAILOVER_HTTP_STATUSES = new Set([502, 503, 504]);

async function fetchWithRuntimeFailover(
  method: "GET" | "POST",
  path: string,
  init: RequestInit,
  allowRetry: boolean,
  signal?: AbortSignal
) {
  await ensureRuntimeEndpointsReady();
  const firstOrigin = getGameRuntimeConfig().apiBaseUrl;
  try {
    const response = await fetch(`${firstOrigin}${path}`, init);
    if (!allowRetry || !FAILOVER_HTTP_STATUSES.has(response.status) || signal?.aborted) return response;
    const nextOrigin = await recoverRuntimeEndpoint("api", firstOrigin);
    return nextOrigin ? fetch(`${nextOrigin}${path}`, init) : response;
  } catch (error) {
    if (!allowRetry || signal?.aborted) throw error;
    const nextOrigin = await recoverRuntimeEndpoint("api", firstOrigin);
    if (!nextOrigin) throw error;
    return fetch(`${nextOrigin}${path}`, init);
  }
}

export interface UnauthorizedRecoveryContext {
  method: "GET" | "POST";
  path: string;
  token: string;
}

export type UnauthorizedRecoveryHandler = (context: UnauthorizedRecoveryContext) => Promise<string | undefined>;
export type CurrentTokenResolver = (token: string) => string | undefined;

let unauthorizedRecoveryHandler: UnauthorizedRecoveryHandler | undefined;
let currentTokenResolver: CurrentTokenResolver | undefined;

export function configureUnauthorizedRecovery(
  handler: UnauthorizedRecoveryHandler,
  resolveCurrentToken?: CurrentTokenResolver
) {
  unauthorizedRecoveryHandler = handler;
  currentTokenResolver = resolveCurrentToken;
  return () => {
    // 只清理当前注册者，避免旧页面卸载时误删新页面刚接管的恢复器。
    if (unauthorizedRecoveryHandler === handler) {
      unauthorizedRecoveryHandler = undefined;
      currentTokenResolver = undefined;
    }
  };
}

export class ApiError extends Error {
  code: number;
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown, code?: number) {
    super(message);
    this.name = "ApiError";
    this.code = code ?? readBusinessCode(payload) ?? RUNTIME_TRANSPORT_CODE.HTTP_ERROR;
    this.status = status;
    this.payload = payload;
  }
}

function readBusinessCode(payload: unknown) {
  if (!payload || typeof payload !== "object") return undefined;
  const code = (payload as Record<string, unknown>).code;
  return typeof code === "number" && Number.isInteger(code) ? code : undefined;
}

function readError(payload: unknown, fallback: string) {
  if (!payload || typeof payload !== "object") return fallback;

  const record = payload as Record<string, unknown>;
  const data = record.data;
  if (typeof record.message === "string") return record.message;
  if (typeof record.error === "string") return record.error;
  if (data && typeof data === "object" && typeof (data as Record<string, unknown>).message === "string") {
    return String((data as Record<string, unknown>).message);
  }

  return fallback;
}

function parsePayload<T>(text: string): T {
  if (!text) return {} as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    return { message: text } as T;
  }
}

function requestTransportError(error: unknown, signal?: AbortSignal) {
  if (signal?.aborted && signal.reason === "llm_timeout") {
    return new ApiError(
      "AI 响应时间过长，本次生成已停止；你可以继续操作或重新尝试。",
      0,
      { reason: "llm_timeout" },
      RUNTIME_TRANSPORT_CODE.REQUEST_CANCELED
    );
  }
  if (
    signal?.aborted ||
    (typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "AbortError")
  ) {
    return new ApiError(
      "请求已取消，请重新发起。",
      0,
      { reason: "request_canceled" },
      RUNTIME_TRANSPORT_CODE.REQUEST_CANCELED
    );
  }
  // 浏览器会把断网、DNS、TLS、CORS 和服务不可达统一折叠为 TypeError，不能伪造更具体的原因。
  if (error instanceof TypeError) {
    return new ApiError(
      "未收到服务器响应，请检查网络、服务地址或跨域配置后重试。",
      0,
      { reason: "network_unreachable" },
      RUNTIME_TRANSPORT_CODE.NETWORK_UNREACHABLE
    );
  }
  const detail = error instanceof Error ? error.message : String(error);
  return new ApiError(
    `请求发送失败：${detail || "未知传输错误"}`,
    0,
    { reason: "request_transport_failed" },
    RUNTIME_TRANSPORT_CODE.REQUEST_FAILED
  );
}

async function readResponsePayload<T>(response: Response): Promise<T> {
  try {
    return parsePayload<T>(await response.text());
  } catch (error) {
    // 已建立连接但响应体读取中断，与完全无法连接必须向用户展示为不同问题。
    throw new ApiError(
      "已连接服务器，但响应读取中断，请重试。",
      response.status,
      {
        reason: "response_read_failed",
        detail: error instanceof Error ? error.message : String(error)
      },
      RUNTIME_TRANSPORT_CODE.RESPONSE_READ_FAILED
    );
  }
}

function throwForResponseError(payload: unknown, response: Response) {
  const businessCode = readBusinessCode(payload);
  // 业务失败即使使用 HTTP 200 也必须抛出；HTTP 非成功仅作为无业务码时的传输兜底。
  if (businessCode !== undefined && businessCode !== CLIENT_BUSINESS_CODE.SUCCESS) {
    throw new ApiError(readError(payload, `业务处理失败（${businessCode}）`), response.status, payload, businessCode);
  }
  if (!response.ok) {
    throw new ApiError(
      readError(payload, `HTTP ${response.status}`),
      response.status,
      payload,
      businessCode ?? RUNTIME_TRANSPORT_CODE.HTTP_ERROR
    );
  }
}

async function requestWithUnauthorizedRecovery<T>(
  method: UnauthorizedRecoveryContext["method"],
  path: string,
  options: HttpOptions,
  send: (token?: string) => Promise<Response>
) {
  // 长任务可能仍持有恢复前的状态快照；发送前改用当前匿名 token，避免每个分段请求重复触发 401。
  const requestToken = options.token ? (currentTokenResolver?.(options.token) ?? options.token) : undefined;
  let response = await send(requestToken);
  let payload = await readResponsePayload<T>(response);

  if (response.status === 401 && requestToken && unauthorizedRecoveryHandler) {
    // 匿名凭证即使设置长有效期，也可能因密钥轮换或账号撤销提前失效；只允许恢复后原请求重试一次。
    const recoveredToken = await unauthorizedRecoveryHandler({
      method,
      path,
      token: requestToken
    });
    if (recoveredToken) {
      response = await send(recoveredToken);
      payload = await readResponsePayload<T>(response);
    }
  }

  throwForResponseError(payload, response);
  return payload;
}

export async function apiPost<T>(path: string, options: HttpOptions = {}): Promise<T> {
  return requestWithUnauthorizedRecovery<T>("POST", path, options, async (token) => {
    try {
      const body = JSON.stringify(options.body ?? {});
      const containsIdempotencyKey = Boolean(
        options.body &&
          typeof options.body === "object" &&
          typeof (options.body as Record<string, unknown>).idempotency_key === "string" &&
          String((options.body as Record<string, unknown>).idempotency_key).trim()
      );
      return await fetchWithRuntimeFailover(
        "POST",
        path,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body,
          signal: options.signal
        },
        options.retryAcrossOrigins === true || containsIdempotencyKey,
        options.signal
      );
    } catch (error) {
      throw requestTransportError(error, options.signal);
    }
  });
}

export async function apiPostStream(path: string, options: HttpOptions = {}): Promise<Response> {
  const send = async (token?: string) => {
    try {
      const body = JSON.stringify(options.body ?? {});
      const containsIdempotencyKey = Boolean(
        options.body && typeof options.body === "object" &&
        typeof (options.body as Record<string, unknown>).idempotency_key === "string" &&
        String((options.body as Record<string, unknown>).idempotency_key).trim()
      );
      return await fetchWithRuntimeFailover(
        "POST",
        path,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream, application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body,
          signal: options.signal
        },
        options.retryAcrossOrigins === true || containsIdempotencyKey,
        options.signal
      );
    } catch (error) {
      throw requestTransportError(error, options.signal);
    }
  };

  const requestToken = options.token ? (currentTokenResolver?.(options.token) ?? options.token) : undefined;
  let response = await send(requestToken);
  if (response.status === 401 && requestToken && unauthorizedRecoveryHandler) {
    const recoveredToken = await unauthorizedRecoveryHandler({ method: "POST", path, token: requestToken });
    if (recoveredToken) response = await send(recoveredToken);
  }
  if (!response.ok) {
    const payload = await readResponsePayload<unknown>(response);
    throwForResponseError(payload, response);
  }
  return response;
}

export async function apiGet<T>(path: string, options: Omit<HttpOptions, "body"> = {}): Promise<T> {
  return requestWithUnauthorizedRecovery<T>("GET", path, options, async (token) => {
    try {
      return await fetchWithRuntimeFailover(
        "GET",
        path,
        {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: options.signal
        },
        true,
        options.signal
      );
    } catch (error) {
      throw requestTransportError(error, options.signal);
    }
  });
}
